import React from 'react';
import { X, FileText, Upload, CreditCard, CheckCircle, Clock, Send, Mail, Phone, Users, Shield, Package, Star, HelpCircle, CalendarDays, RefreshCw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const stepIconMap = {
  FileText: FileText,
  Upload: Upload,
  CreditCard: CreditCard,
  CheckCircle: CheckCircle,
  Clock: Clock,
  Send: Send,
  Mail: Mail,
  Phone: Phone,
  Users: Users,
  Shield: Shield,
  Package: Package,
  Star: Star
};

const ServiceStepsModal = ({ service, onClose }) => {
  const { t, language } = useLanguage();

  const getStepTitle = (step) => {
    if (language === 'ru' && step.title_ru) return step.title_ru;
    if (language === 'uz' && step.title_uz) return step.title_uz;
    return step.title;
  };

  const getStepDesc = (step) => {
    if (language === 'ru' && step.description_ru) return step.description_ru;
    if (language === 'uz' && step.description_uz) return step.description_uz;
    return step.description;
  };

  const isDubaiVisa = service?.name?.toLowerCase().includes('dubai') || 
    (service?.name?.toLowerCase().includes('visa') && 
     !service?.name?.toLowerCase().includes('change') && 
     !service?.name?.toLowerCase().includes('freelance') &&
     !service?.name?.toLowerCase().includes('family'));

  const isVisaChange = service?.name?.toLowerCase().includes('change');
  const isFamilyVisa = service?.name?.toLowerCase().includes('family');
  const isFreelanceVisa = service?.name?.toLowerCase().includes('freelance');

  // Built-in Dubai Visa info steps
  const dubaiVisaSteps = isDubaiVisa ? [
    { icon: HelpCircle, title: t('dubaiStep1Title'), description: t('dubaiStep1Desc') },
    { icon: CalendarDays, title: t('dubaiStep2Title'), description: t('dubaiStep2Desc') },
    { icon: RefreshCw, title: t('dubaiStep3Title'), description: t('dubaiStep3Desc') }
  ] : [];

  // Built-in Family Visa info steps
  const familyVisaSteps = isFamilyVisa ? [
    { icon: Users, title: t('familyVStep1Title') || 'Select Family Member', description: t('familyVStep1Desc') || 'Choose who you want to apply the Family Visa for: Husband, Wife, Son, Daughter, or Mother.' },
    { icon: Upload, title: t('familyVStep2Title') || 'Upload Documents', description: t('familyVStep2Desc') || 'Upload the passport copy and a personal photo of the family member you are applying for.' },
    { icon: Shield, title: t('familyVStep3Title') || 'Emirates ID Preview', description: t('familyVStep3Desc') || 'Our system generates an Emirates ID preview from the passport data. Verify that all details are correct before proceeding.' },
    { icon: Phone, title: t('familyVStep4Title') || 'Contact Information', description: t('familyVStep4Desc') || 'Provide your WhatsApp number and email address so our team can reach you with updates on your application.' },
    { icon: CheckCircle, title: t('familyVStep5Title') || 'Review & Confirm', description: t('familyVStep5Desc') || 'Review all the details of your Family Visa application and confirm the submission. You will receive a tracking number to check the status.' }
  ] : [];

  // Built-in Freelance Visa info steps
  const freelanceVisaSteps = isFreelanceVisa ? [
    { icon: FileText, title: t('freelanceVStep1Title') || 'Submit Application', description: t('freelanceVStep1Desc') || 'Fill in your personal details, upload your passport copy and photo. Our team will begin processing your freelance visa.' },
    { icon: CheckCircle, title: t('freelanceVStep2Title') || 'Visa Approved', description: t('freelanceVStep2Desc') || 'Your freelance visa application is reviewed and approved. You will receive confirmation with your visa approval document.' },
    { icon: RefreshCw, title: t('freelanceVStep3Title') || 'Option Change', description: t('freelanceVStep3Desc') || 'Your visa status is updated in the system. Required option changes are processed for your freelance permit.' },
    { icon: Shield, title: t('freelanceVStep4Title') || 'Medical Test', description: t('freelanceVStep4Desc') || 'Complete the mandatory medical fitness test at an approved health center. Results are submitted to immigration.' },
    { icon: FileText, title: t('freelanceVStep5Title') || 'Residence Visa', description: t('freelanceVStep5Desc') || 'Your UAE residence visa is issued. This allows you to live and work legally in the UAE as a freelancer.' },
    { icon: CreditCard, title: t('freelanceVStep6Title') || 'Emirates ID', description: t('freelanceVStep6Desc') || 'Your Emirates ID is processed and issued. This is your official UAE identity document for all transactions.' }
  ] : [];

  // Built-in Visa Change steps
  const visaChangeSteps = isVisaChange ? [
    { icon: HelpCircle, title: t('vcStep1Title') || 'Choose Travel Method', description: t('vcStep1Desc') || 'Select how you want to do your visa change.' },
    { icon: Upload, title: t('vcStep2Title') || 'Upload Documents', description: t('vcStep2Desc') || 'Upload your passport copy and a personal photo.' },
    { icon: RefreshCw, title: t('vcStep3Title') || 'Travel & Border Processing', description: t('vcStep3Desc') || 'Travel to the border or airport as planned.' },
    { icon: CheckCircle, title: t('vcStep4Title') || 'New Visa Issued', description: t('vcStep4Desc') || 'After re-entry, your new visa is stamped and activated.' }
  ] : [];

  const builtInSteps = [...dubaiVisaSteps, ...visaChangeSteps, ...familyVisaSteps, ...freelanceVisaSteps];
  const adminSteps = service?.steps || [];
  const hasContent = builtInSteps.length > 0 || adminSteps.length > 0;

  if (!service || !hasContent) return null;

  // Translate service name
  const serviceTranslationMap = {
    "Dubai Visa 14 Days": "dubaiVisa14Days",
    "Dubai Visa 30 Days": "dubaiVisa30Days",
    "Dubai Visa 60 Days": "dubaiVisa60Days",
    "Dubai Visa": "dubaiVisa30Days",
    "Freelance Visa": "freelanceVisaName",
    "Family Visa": "familyVisaName",
    "30 Days Visa Change": "visaChange30Days",
    "60 Days Visa Change": "visaChange60Days",
    "Visa Change": "visaChange60Days"
  };
  const nameKey = serviceTranslationMap[service.name];
  const displayName = nameKey ? t(nameKey) : service.name;

  return (
    <div className="service-steps-modal-overlay" onClick={onClose}>
      <div className="service-steps-modal" onClick={e => e.stopPropagation()}>
        <button className="steps-modal-close" onClick={onClose}>
          <X size={24} />
        </button>
        
        <div className="steps-modal-header">
          <h2>{displayName}</h2>
          <p>{t('howItWorks') || 'How It Works'}</p>
        </div>

        <div className="steps-modal-content">
          {/* Built-in steps (Dubai Visa or Family Visa) */}
          {builtInSteps.map((step, index) => {
            const IconComp = step.icon;
            return (
              <div key={`builtin-${index}`} className="public-step-item">
                <div className="public-step-number">{index + 1}</div>
                <div className="public-step-icon">
                  <IconComp size={24} />
                </div>
                <div className="public-step-content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </div>
            );
          })}

          {/* Admin-defined steps */}
          {adminSteps.map((step, index) => {
            const IconComponent = stepIconMap[step.icon] || FileText;
            const stepNum = builtInSteps.length + index + 1;
            return (
              <div key={`admin-${index}`} className="public-step-item">
                <div className="public-step-number">{stepNum}</div>
                <div className="public-step-icon">
                  <IconComponent size={24} />
                </div>
                <div className="public-step-content">
                  <h3>{getStepTitle(step)}</h3>
                  <p>{getStepDesc(step)}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="steps-modal-footer">
          <button className="btn-primary" onClick={onClose}>
            {t('gotIt') || 'Got It'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceStepsModal;
